import { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '@/firebase-config';
import { useAppStore } from '@/store/useAppStore';
import { showToast } from '@/features/layout/notifications';
import type { AppNotification } from '../types';

const MAX_NOTIFICATIONS = 50;

interface NotificationState {
  subscriptionKey: string | null;
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
}

const EMPTY_NOTIFICATION_STATE: NotificationState = {
  subscriptionKey: null,
  notifications: [],
  unreadCount: 0,
  loading: false,
};

export function useNotifications() {
  const user = useAppStore(state => state.auth.user);
  const orgId = useAppStore(state => state.cloud.orgId);
  const projectId = useAppStore(state => state.cloud.projectId);
  const subscriptionKey = user?.uid && orgId && projectId
    ? `${user.uid}:${orgId}:${projectId}`
    : null;
  const [notificationState, setNotificationState] = useState<NotificationState>(EMPTY_NOTIFICATION_STATE);
  const prevCountRef = useRef(0);
  const hasLoadedInitialSnapshotRef = useRef(false);

  useEffect(() => {
    if (!subscriptionKey || !user?.uid || !orgId || !projectId) {
      prevCountRef.current = 0;
      hasLoadedInitialSnapshotRef.current = false;
      return;
    }

    prevCountRef.current = 0;
    hasLoadedInitialSnapshotRef.current = false;

    const q = query(
      collection(db, 'users', user.uid, 'notifications'),
      where('orgId', '==', orgId),
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc'),
      limit(MAX_NOTIFICATIONS),
    );

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }) as AppNotification)
        .filter((notification) => (
          notification.orgId === orgId && notification.projectId === projectId
        ));

      const newUnread = items.filter((n) => !n.read).length;

      // Toast on new arrivals (only after initial load)
      // Design doc specifies 5-second auto-dismiss
      if (hasLoadedInitialSnapshotRef.current && newUnread > prevCountRef.current) {
        const newest = items.find((n) => !n.read);
        if (newest) {
          showToast(newest.title, 'info', 5000);
        }
      }

      prevCountRef.current = newUnread;
      hasLoadedInitialSnapshotRef.current = true;
      setNotificationState({
        subscriptionKey,
        notifications: items,
        unreadCount: newUnread,
        loading: false,
      });
    }, (error) => {
      // Gracefully handle Firestore errors (e.g., permission-denied
      // before security rules are deployed) so the UI shows the empty
      // state instead of staying stuck on "Loading..." forever.
      console.warn('[useNotifications] Firestore listener error:', error.code);
      setNotificationState({
        subscriptionKey,
        notifications: [],
        unreadCount: 0,
        loading: false,
      });
    });

    return unsub;
  }, [subscriptionKey, user?.uid, orgId, projectId]);

  if (!subscriptionKey) {
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
    };
  }

  if (notificationState.subscriptionKey !== subscriptionKey) {
    return {
      notifications: [],
      unreadCount: 0,
      loading: true,
    };
  }

  return {
    notifications: notificationState.notifications,
    unreadCount: notificationState.unreadCount,
    loading: notificationState.loading,
  };
}
